// app/frontend/components/layouts/AdminLayout.tsx
import { ReactNode, useState } from "react";
import {
  AppShell,
  NavLink,
  Group,
  Box,
  Burger,
  ScrollArea,
  Divider,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconDashboard,
  IconVideo,
  IconCategory,
  IconUsers,
  IconRoute,
  IconNews,
  IconArticle,
  IconArrowLeft,
  IconSettings,
} from "@tabler/icons-react";
import { Link, usePage } from "@inertiajs/react";
import { L2KH4, L2KH6, L2KCaption } from "../ui/Typography";

interface AdminLayoutProps {
  children: ReactNode;
}

const navItems = [
  { label: "Dashboard", href: "/admin", icon: IconDashboard },
  { label: "Videos", href: "/admin/videos", icon: IconVideo },
  { label: "Posts", href: "/admin/posts", icon: IconArticle },
  { label: "Categories", href: "/admin/categories", icon: IconCategory },
  { label: "Users", href: "/admin/users", icon: IconUsers },
  { label: "Learning Tracks", href: "/admin/learning_tracks", icon: IconRoute },
  { label: "Changelog", href: "/admin/changelog_entries", icon: IconNews },
];

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [opened, { toggle }] = useDisclosure();
  const { url } = usePage();

  const isActive = (href: string) => {
    if (href === "/admin") {
      return url === "/admin" || url === "/admin/";
    }
    return url.startsWith(href);
  };

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 250,
        breakpoint: "sm",
        collapsed: { mobile: !opened },
      }}
      padding="md"
      styles={{
        main: {
          background: `
            radial-gradient(ellipse at 20% 50%, rgba(255, 152, 0, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 20%, rgba(255, 193, 7, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse at 40% 80%, rgba(255, 87, 34, 0.1) 0%, transparent 50%),
            linear-gradient(135deg, #171717 0%, #1a1a1a 25%, #171717 50%, #1f1f1f 75%, #171717 100%)
          `,
          minHeight: "100vh",
        },
        header: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          borderBottom: "1px solid rgba(255, 152, 0, 0.3)",
          backdropFilter: "blur(10px)",
        },
        navbar: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          borderRight: "1px solid rgba(255, 152, 0, 0.3)",
          backdropFilter: "blur(10px)",
        },
      }}
    >
      {/* Header */}
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              color="#FF9800"
            />
            <L2KH4 mb={0}>
              <span style={{ color: "#FF9800" }}>Learntokiz</span>{" "}
              <span style={{ color: "#888", fontWeight: 400 }}>Admin</span>
            </L2KH4>
          </Group>
          <Link href="/">
            <Group gap="xs" style={{ color: "#888", cursor: "pointer" }}>
              <IconArrowLeft size={16} />
              <L2KCaption>Back to Site</L2KCaption>
            </Group>
          </Link>
        </Group>
      </AppShell.Header>

      {/* Sidebar */}
      <AppShell.Navbar p="md">
        <AppShell.Section grow component={ScrollArea}>
          <L2KCaption variant="tertiary" mb="xs" tt="uppercase" fw={600}>
            Management
          </L2KCaption>
          {navItems.map((item) => (
            <NavLink
              key={item.href}
              component={Link}
              href={item.href}
              label={item.label}
              leftSection={<item.icon size={18} />}
              active={isActive(item.href)}
              styles={{
                root: {
                  borderRadius: "8px",
                  marginBottom: "4px",
                  color: isActive(item.href) ? "#171717" : "#D0D0D0",
                  backgroundColor: isActive(item.href)
                    ? "#FF9800"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: isActive(item.href)
                      ? "#FF9800"
                      : "rgba(255, 152, 0, 0.1)",
                    color: isActive(item.href) ? "#171717" : "#FF9800",
                  },
                },
                label: {
                  fontFamily: "'Public Sans', sans-serif",
                  fontWeight: 500,
                },
              }}
            />
          ))}
        </AppShell.Section>

        <AppShell.Section>
          <Divider my="sm" color="rgba(255, 255, 255, 0.1)" />
          <NavLink
            component={Link}
            href="/admin/settings"
            label="Settings"
            leftSection={<IconSettings size={18} />}
            styles={{
              root: {
                borderRadius: "8px",
                color: "#888",
                "&:hover": {
                  backgroundColor: "rgba(255, 152, 0, 0.1)",
                  color: "#FF9800",
                },
              },
              label: {
                fontFamily: "'Public Sans', sans-serif",
                fontWeight: 500,
              },
            }}
          />
        </AppShell.Section>
      </AppShell.Navbar>

      {/* Main Content */}
      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  );
}
